import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Settings, RotateCw, Target, Plus, Search, ArrowRight, BarChart3, Code } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeInfo, setStoreInfo] = useState(null);

  const storeId = localStorage.getItem('store_id');

  useEffect(() => {
    if (!storeId) {
      navigate('/');
      return;
    }

    fetchProducts();
    fetchStoreInfo();
  }, [storeId, navigate]);

  const fetchStoreInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/store/${storeId}`);
      setStoreInfo(response.data);
    } catch (error) {
      console.error('Error fetching store info:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products`, {
        headers: {
          'X-Store-Id': storeId
        }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800 border-green-200',
      configured: 'bg-blue-100 text-blue-800 border-blue-200',
      pending: 'bg-zinc-100 text-zinc-800 border-zinc-200'
    };
    const labels = {
      active: 'Activo',
      configured: 'Configurado',
      pending: 'Pendiente'
    };
    return (
      <Badge className={variants[status] || variants.pending} data-testid={`product-status-${status}`}>
        {labels[status] || labels.pending}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/fusion2v-logo.png" alt="Fusion2V Logo" className="h-10 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-zinc-900">Spin 360° Fusion2V</h1>
                {storeInfo && (
                  <p className="text-sm text-zinc-500">{storeInfo.store_name || 'Mi Tienda'}</p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/widget')}
                variant="outline"
                className="gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-none hover:from-purple-600 hover:to-indigo-600"
                data-testid="widget-button"
              >
                <Code className="w-4 h-4" />
                Instalar Widget
              </Button>
              <Button
                onClick={() => navigate('/analytics')}
                variant="outline"
                className="gap-2"
                data-testid="analytics-button"
              >
                <BarChart3 className="w-4 h-4" />
                Analytics
              </Button>
              <Button
                onClick={() => navigate('/settings')}
                variant="outline"
                className="gap-2"
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4" />
                Configuración
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-300" data-testid="stats-total-products">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 font-medium uppercase tracking-wide">Total Productos</p>
                <p className="text-3xl font-bold text-zinc-900">{products.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-300" data-testid="stats-configured">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <RotateCw className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 font-medium uppercase tracking-wide">Con Spin 360°</p>
                <p className="text-3xl font-bold text-zinc-900">
                  {products.filter(p => p.spin_configured).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-300" data-testid="stats-hotspots">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 font-medium uppercase tracking-wide">Con Hotspots</p>
                <p className="text-3xl font-bold text-zinc-900">
                  {products.filter(p => p.hotspots_count > 0).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <Input
              type="text"
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="product-search-input"
            />
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="products-loading">
            <RotateCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-zinc-200" data-testid="no-products">
            <Package className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-800 mb-2">No hay productos</h3>
            <p className="text-zinc-500">No se encontraron productos en tu tienda</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-xl border border-zinc-200 shadow-sm hover:shadow-lg hover:border-primary/50 transition-all duration-300 overflow-hidden cursor-pointer"
                onClick={() => navigate(`/product/${product.id}`)}
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-square bg-zinc-100 relative overflow-hidden">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-16 h-16 text-zinc-300" />
                    </div>
                  )}
                  {product.spin_configured && (
                    <div className="absolute top-3 right-3 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
                      <RotateCw className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-zinc-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between">
                    {getStatusBadge(product.status)}
                    {product.hotspots_count > 0 && (
                      <div className="flex items-center gap-1 text-sm text-zinc-600">
                        <Target className="w-4 h-4" />
                        <span>{product.hotspots_count}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Configurar</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}